import { useEffect, useState, useCallback, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LogOut, Inbox, FileText, Trash2, Upload, Loader2, ExternalLink, Calendar,
  Mail, Phone, Building2, Filter, Users, PenSquare,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import { api, API_BASE, formatApiError } from "@/lib/api";
import { DOC_TAXONOMY } from "@/lib/documents";
import usePageTitle from "@/lib/usePageTitle";
import ContentEditor from "@/pages/admin/ContentEditor";

const MAX_MB = 25;
const BACKEND_ROOT = API_BASE.replace(/\/api$/, "");

export default function AdminDashboard() {
  usePageTitle("Admin Dashboard");
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("inquiries");

  const onLogout = async () => {
    await logout();
    navigate("/admin/login", { replace: true });
  };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#F8F7F4]">
      {/* Top bar */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#F8F7F4]/90 border-b border-[#1A1A1A]/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-5 flex items-center justify-between gap-4">
          <Link to="/" className="flex flex-col leading-none">
            <span className="font-serif-display text-xl tracking-[0.18em] uppercase">GESL</span>
            <span className="text-[10px] tracking-[0.3em] uppercase text-[#5C5A56] mt-0.5">Admin Console</span>
          </Link>
          <div className="flex items-center gap-4">
            <div className="hidden sm:flex flex-col items-end">
              <span className="text-xs text-[#5C5A56]">Signed in as</span>
              <span data-testid="admin-user-email" className="text-sm font-medium">{user?.email}</span>
            </div>
            <button
              data-testid="admin-logout-button"
              onClick={onLogout}
              className="btn-ghost text-sm"
            >
              <LogOut size={15} /> Sign out
            </button>
          </div>
        </div>
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 flex gap-1 border-t border-[#1A1A1A]/5 overflow-x-auto">
          <TabBtn id="inquiries" tab={tab} setTab={setTab} icon={Inbox} label="Inquiries" testid="tab-inquiries" />
          <TabBtn id="financial" tab={tab} setTab={setTab} icon={FileText} label="Financial Statements" testid="tab-financial" />
          <TabBtn id="shareholder" tab={tab} setTab={setTab} icon={Users} label="Shareholder Information" testid="tab-shareholder" />
          <TabBtn id="content" tab={tab} setTab={setTab} icon={PenSquare} label="Site Content" testid="tab-content" />
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 md:px-12 py-10">
        {tab === "inquiries" && <InquiriesPanel />}
        {tab === "financial" && <DocumentsPanel section="financial" />}
        {tab === "shareholder" && <DocumentsPanel section="shareholder" />}
        {tab === "content" && <ContentEditor />}
      </main>
    </div>
  );
}

function TabBtn({ id, tab, setTab, icon: Icon, label, testid }) {
  const active = tab === id;
  return (
    <button
      data-testid={testid}
      onClick={() => setTab(id)}
      className={`flex items-center gap-2 px-4 py-3 text-sm border-b-2 transition-colors whitespace-nowrap ${active ? "border-[#C25934] text-[#1A1A1A] font-medium" : "border-transparent text-[#5C5A56] hover:text-[#1A1A1A]"}`}
    >
      <Icon size={15} /> {label}
    </button>
  );
}

/* ---------- Inquiries ---------- */
function InquiriesPanel() {
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/inquiries");
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
      setItems([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onDelete = async (id) => {
    if (!window.confirm("Delete this inquiry?")) return;
    try {
      await api.delete(`/inquiries/${id}`);
      setItems((arr) => arr.filter((i) => i.id !== id));
      toast.success("Inquiry deleted");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  const visible = (items || []).filter((i) => {
    if (!filter) return true;
    const q = filter.toLowerCase();
    return [i.name, i.email, i.company, i.message, i.yarn_type].filter(Boolean).some((v) => v.toLowerCase().includes(q));
  });

  return (
    <section data-testid="inquiries-panel">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="overline mb-2">Inbox</div>
          <h2 className="font-serif-display text-3xl md:text-4xl">Customer inquiries</h2>
          <p className="text-sm text-[#5C5A56] mt-2">
            {items === null ? "Loading…" : `${items.length} total submission${items.length === 1 ? "" : "s"}`}
          </p>
        </div>
        <div className="relative w-full max-w-xs">
          <Filter size={14} className="absolute left-0 bottom-3 text-[#5C5A56]" />
          <input
            data-testid="inquiry-filter"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="input-editorial pl-6"
            placeholder="Filter by name, email, yarn…"
          />
        </div>
      </div>

      {items === null && (
        <div className="text-sm text-[#5C5A56] flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading inquiries…</div>
      )}

      {items !== null && visible.length === 0 && (
        <div className="bg-[#EAE6DF] p-10 text-center text-[#5C5A56]">
          {items.length === 0 ? "No inquiries yet." : "No inquiries match your filter."}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {visible.map((i) => (
          <article key={i.id} data-testid={`inquiry-card-${i.id}`} className="editorial-card p-7">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="overline text-[#C25934]">{i.yarn_type || "General Inquiry"}</div>
                <h3 className="font-serif-display text-2xl mt-2">{i.name}</h3>
                {i.company && (
                  <div className="flex items-center gap-2 text-sm text-[#5C5A56] mt-1">
                    <Building2 size={13} /> {i.company}
                  </div>
                )}
              </div>
              <button
                data-testid={`delete-inquiry-${i.id}`}
                onClick={() => onDelete(i.id)}
                className="p-2 text-[#5C5A56] hover:text-[#C25934] hover:bg-[#EAE6DF] transition-colors"
                title="Delete"
              >
                <Trash2 size={15} />
              </button>
            </div>

            <div className="mt-4 space-y-1.5 text-sm text-[#5C5A56]">
              <div className="flex items-center gap-2"><Mail size={13} className="text-[#C25934]" /> <a href={`mailto:${i.email}`} className="link-underline text-[#1A1A1A]">{i.email}</a></div>
              {i.phone && <div className="flex items-center gap-2"><Phone size={13} className="text-[#C25934]" /> {i.phone}</div>}
              <div className="flex items-center gap-2"><Calendar size={13} className="text-[#C25934]" /> {new Date(i.created_at).toLocaleString()}</div>
            </div>

            {(i.count_range || i.quantity) && (
              <div className="mt-4 flex flex-wrap gap-2 text-xs">
                {i.count_range && <span className="bg-[#EAE6DF] px-3 py-1 font-mono-ed tracking-wider">{i.count_range}</span>}
                {i.quantity && <span className="bg-[#EAE6DF] px-3 py-1 font-mono-ed tracking-wider">{i.quantity}</span>}
              </div>
            )}

            <div className="mt-5 pt-5 border-t border-[#1A1A1A]/10 text-sm leading-relaxed whitespace-pre-wrap">
              {i.message}
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

/* ---------- Documents (sectioned) ---------- */
function DocumentsPanel({ section }) {
  const sectionDef = DOC_TAXONOMY[section];
  const categories = sectionDef.categories;
  const [active, setActive] = useState(categories[0].slug);
  const [docs, setDocs] = useState(null);

  const load = useCallback(async () => {
    setDocs(null);
    try {
      const { data } = await api.get("/documents", { params: { section } });
      setDocs(data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
      setDocs([]);
    }
  }, [section]);

  useEffect(() => { load(); }, [load]);

  // Reset active to first category when switching section
  useEffect(() => { setActive(categories[0].slug); }, [section, categories]);

  const grouped = useMemo(() => {
    const g = {};
    for (const c of categories) g[c.slug] = [];
    for (const d of docs || []) (g[d.category] ||= []).push(d);
    return g;
  }, [docs, categories]);

  const onUploaded = (newDoc) => {
    setDocs((arr) => [newDoc, ...(arr || [])]);
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this document?")) return;
    try {
      await api.delete(`/admin/documents/${id}`);
      setDocs((arr) => (arr || []).filter((d) => d.id !== id));
      toast.success("Document deleted");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  const activeCategoryLabel = categories.find((c) => c.slug === active)?.label;
  const activeDocs = grouped[active] || [];

  return (
    <section data-testid={`docs-panel-${section}`}>
      <div className="mb-8">
        <div className="overline mb-2">{sectionDef.label}</div>
        <h2 className="font-serif-display text-3xl md:text-4xl">{sectionDef.label}</h2>
        <p className="text-sm text-[#5C5A56] mt-2">
          PDF only · max {MAX_MB} MB · uploads appear publicly under the matching tab on the website.
        </p>
      </div>

      {/* Category sub-tabs */}
      <div data-testid={`subtabs-${section}`} className="flex flex-wrap gap-x-1 gap-y-2 border-b border-[#1A1A1A]/15 mb-8 overflow-x-auto">
        {categories.map((c) => (
          <button
            key={c.slug}
            data-testid={`subtab-${section}-${c.slug}`}
            onClick={() => setActive(c.slug)}
            className={`px-4 py-3 text-sm tracking-wide border-b-2 -mb-px transition-colors whitespace-nowrap ${
              active === c.slug
                ? "border-[#C25934] text-[#1A1A1A] font-medium"
                : "border-transparent text-[#5C5A56] hover:text-[#1A1A1A]"
            }`}
          >
            {c.label}
            <span className="ml-2 font-mono-ed text-[10px] text-[#5C5A56]">
              {grouped[c.slug]?.length ?? 0}
            </span>
          </button>
        ))}
      </div>

      <UploadForm
        section={section}
        category={active}
        categoryLabel={activeCategoryLabel}
        onUploaded={onUploaded}
      />

      <div className="border border-[#1A1A1A]/10 bg-[#F8F7F4] mt-8">
        <div className="hidden md:grid grid-cols-12 px-7 py-4 bg-[#EAE6DF] border-b border-[#1A1A1A]/10 overline">
          <div className="col-span-3">Period</div>
          <div className="col-span-6">Title / File</div>
          <div className="col-span-1">Size</div>
          <div className="col-span-2 text-right">Actions</div>
        </div>
        {docs === null ? (
          <div className="p-8 text-sm text-[#5C5A56] flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</div>
        ) : activeDocs.length === 0 ? (
          <div className="p-10 text-center text-[#5C5A56]">
            No {activeCategoryLabel} uploaded yet.
          </div>
        ) : activeDocs.map((r) => (
          <div key={r.id} data-testid={`admin-doc-row-${r.id}`} className="grid grid-cols-1 md:grid-cols-12 px-7 py-5 border-b border-[#1A1A1A]/10 last:border-b-0 items-center gap-3">
            <div className="md:col-span-3 flex items-center gap-2">
              <Calendar size={14} className="text-[#C25934]" />
              <span className="font-mono-ed text-xs tracking-wider">{r.period || "—"}</span>
            </div>
            <div className="md:col-span-6">
              <div className="font-serif-display text-lg leading-tight">{r.title}</div>
              <div className="text-xs text-[#5C5A56] font-mono-ed mt-1">{r.file_name}</div>
            </div>
            <div className="md:col-span-1 text-xs text-[#5C5A56] font-mono-ed">
              {(r.size_bytes / 1024).toFixed(0)} KB
            </div>
            <div className="md:col-span-2 md:text-right flex md:justify-end gap-2">
              <a
                href={`${BACKEND_ROOT}${r.download_url}`}
                target="_blank"
                rel="noreferrer"
                data-testid={`open-doc-${r.id}`}
                className="p-2 text-[#5C5A56] hover:text-[#1A1A1A] hover:bg-[#EAE6DF] transition-colors"
                title="Open"
              >
                <ExternalLink size={15} />
              </a>
              <button
                data-testid={`delete-doc-${r.id}`}
                onClick={() => onDelete(r.id)}
                className="p-2 text-[#5C5A56] hover:text-[#C25934] hover:bg-[#EAE6DF] transition-colors"
                title="Delete"
              >
                <Trash2 size={15} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function UploadForm({ section, category, categoryLabel, onUploaded }) {
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [period, setPeriod] = useState("");
  const [uploading, setUploading] = useState(false);

  // Reset form when category changes
  useEffect(() => {
    setFile(null);
    setTitle("");
    setPeriod("");
    const el = document.getElementById(`upload-file-${section}-${category}`);
    if (el) el.value = "";
  }, [section, category]);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) { setFile(null); return; }
    if (f.type !== "application/pdf") {
      toast.error("Only PDF files are allowed.");
      e.target.value = "";
      return;
    }
    if (f.size > MAX_MB * 1024 * 1024) {
      toast.error(`File exceeds ${MAX_MB} MB limit.`);
      e.target.value = "";
      return;
    }
    setFile(f);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Please choose a PDF file.");
    if (!title.trim()) return toast.error("Title is required.");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("section", section);
      fd.append("category", category);
      fd.append("title", title.trim());
      if (period.trim()) fd.append("period", period.trim());
      const { data } = await api.post("/admin/documents", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onUploaded(data);
      setFile(null);
      setTitle("");
      setPeriod("");
      const el = document.getElementById(`upload-file-${section}-${category}`);
      if (el) el.value = "";
      toast.success(`Uploaded to ${categoryLabel}.`);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  return (
    <form
      data-testid={`upload-form-${section}-${category}`}
      onSubmit={onSubmit}
      className="bg-[#EAE6DF] p-7 md:p-10 border border-[#1A1A1A]/10"
    >
      <div className="flex items-center gap-3 mb-6">
        <div className="overline">Upload to</div>
        <div className="font-serif-display text-2xl">{categoryLabel}</div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
        <div className="md:col-span-2">
          <label className="block text-[10px] tracking-[0.25em] uppercase text-[#5C5A56] mb-2 font-semibold">
            PDF File *
          </label>
          <input
            id={`upload-file-${section}-${category}`}
            data-testid={`upload-file-input-${section}-${category}`}
            type="file"
            accept="application/pdf"
            onChange={onFile}
            className="block w-full text-sm file:mr-4 file:py-2.5 file:px-4 file:border-0 file:bg-[#1A1A1A] file:text-[#F8F7F4] file:font-medium hover:file:bg-[#2E4A3D] file:cursor-pointer cursor-pointer"
          />
          {file && (
            <div className="mt-2 text-xs text-[#5C5A56]">
              Selected: <span className="font-mono-ed">{file.name}</span> · {(file.size / 1024).toFixed(1)} KB
            </div>
          )}
        </div>

        <Field label="Title *">
          <input
            data-testid={`upload-title-${section}-${category}`}
            required
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="input-editorial"
            placeholder={`${categoryLabel} – FY 2024-25`}
          />
        </Field>

        <Field label="Period (optional)">
          <input
            data-testid={`upload-period-${section}-${category}`}
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="input-editorial"
            placeholder="FY 2024 – 25 / Q4 · FY 2024 – 25"
          />
        </Field>
      </div>

      <div className="mt-8">
        <button
          data-testid={`upload-submit-${section}-${category}`}
          type="submit"
          disabled={uploading}
          className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {uploading ? <><Loader2 size={16} className="animate-spin" /> Uploading…</> : <><Upload size={16} /> Upload to {categoryLabel}</>}
        </button>
      </div>
    </form>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[10px] tracking-[0.25em] uppercase text-[#5C5A56] mb-2 font-semibold">{label}</label>
      {children}
    </div>
  );
}
